import { createTheme } from '@mui/material/styles';
import { accentAlpha, tokens } from './tokens';

export function createAppTheme() {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: tokens.colors.accent,
        dark: tokens.colors.accentHover,
        light: tokens.colors.accent2,
        contrastText: '#ffffff',
      },
      secondary: {
        main: tokens.colors.accent2,
        contrastText: '#ffffff',
      },
      background: {
        default: tokens.colors.bg,
        paper: tokens.colors.bgElevated,
      },
      text: {
        primary: tokens.colors.text,
        secondary: tokens.colors.textMuted,
      },
      divider: tokens.colors.border,
      action: {
        hover: 'rgba(255, 255, 255, 0.06)',
        selected: accentAlpha(0.16),
      },
    },
    typography: {
      fontFamily: 'var(--font-sans)',
      h1: { fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
      body1: { lineHeight: 1.7 },
    },
    shape: {
      borderRadius: tokens.radii.button,
    },
    components: {
      // ButtonBase sets outline: 0 on its own class, which beats the global focus rule below.
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${tokens.colors.accent}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          body: {
            backgroundColor: tokens.colors.bg,
            color: tokens.colors.text,
          },
          '::selection': {
            backgroundColor: accentAlpha(0.35),
          },
          '*:focus-visible': {
            outline: `2px solid ${tokens.colors.accent}`,
            outlineOffset: 2,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.colors.bgElevated,
            backgroundImage: 'none',
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: 12,
            boxShadow: tokens.shadows.dialog,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backgroundImage: 'none',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${tokens.colors.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: tokens.colors.bgElevated,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.colors.surface,
            borderRadius: tokens.radii.card,
            border: `1px solid ${tokens.colors.border}`,
            boxShadow: tokens.shadows.card,
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: tokens.radii.button,
            fontWeight: 600,
            textTransform: 'none',
            transitionProperty: 'transform, background-color, box-shadow, border-color, color',
            transitionDuration: '150ms',
            transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
            '&:active': {
              transform: 'scale(0.96)',
            },
          },
          containedPrimary: {
            boxShadow: tokens.shadows.button,
            '&:hover': {
              backgroundColor: tokens.colors.accentHover,
              boxShadow: tokens.shadows.buttonHover,
            },
          },
          outlined: {
            borderColor: tokens.colors.borderStrong,
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.28)',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radii.chip,
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: tokens.colors.text,
            border: `1px solid ${tokens.colors.borderStrong}`,
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.colors.surface,
            borderRadius: 12,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.borderStrong,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.28)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.accent,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.colors.bgElevated,
            backgroundImage: 'none',
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: tokens.radii.card,
            boxShadow: tokens.shadows.dialog,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#1a1a1a',
            border: `1px solid ${tokens.colors.border}`,
            fontSize: '0.8rem',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.colors.border,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none',
          },
          standardInfo: {
            backgroundColor: accentAlpha(0.1),
            border: `1px solid ${accentAlpha(0.25)}`,
            color: tokens.colors.text,
          },
        },
      },
    },
  });
}

export const appTheme = createAppTheme();
